import {
  Flex,
  Divider,
  Text,
  Tooltip,
  Box,
  Icon,
  useTheme,
} from '@chakra-ui/react';

import { useDashboardStore } from '../../../hooks/useDashboardStore';
import { useTeamComparisonGql } from '../../graphql';
import {
  InfoIcon,
  SortAsscending,
  SortDescending,
  SortIcon,
} from '../../../icons';
import { useTeamTableContext } from '../../context/TeamTableContext';
import TrendCell from '../../../tableComponents/common/columns/MetricCol/TrendCell';
import MetricLoadingCell from '../LoadingCell/MetricLoadingCell';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DATA_POINT_KEYS } from '../../../shared/utils';
import useTeamTableStore from '../../../hooks/useTeamTableStore';
import { MetricTooltip } from '../../../tableComponents';
import { TeamDto, TeamMetricRow } from '../../types/team-table.types';
interface MetricState {
  metricTitle: string;
  metricKey: string;
  sequence: number;
  id?: string;
}
type Props = {
  metricList: MetricTooltip[];
  metricTooltip: MetricTooltip[];
  isTeamsDataLoading: boolean;
  selectedMetric: MetricState;
  selectedMetrics: MetricState[];
  teamParentMap: Record<string, string | null>;
  startDate?: string;
  endDate?: string;
  compareTo?: string;
  initialOrderBy?: string;
  orderColumn?: string;
  setOrderColumn?: (orderColumn: string) => void;
  setOrderByUrl?: (orderBy: string) => void;
  currentPage?: number;
  setCurrentPage?: (page: number) => void;
  setTotalPages?: (totalPages: number) => void;
  scrollToTop?: () => void;
  teams: string[];
  teamData: TeamDto[];
};

const TeamMetric = ({
  metricList,
  metricTooltip,
  selectedMetrics,
  selectedMetric,
  isTeamsDataLoading,
  teamParentMap,
  startDate,
  endDate,
  compareTo,
  initialOrderBy,
  orderColumn = '',
  setOrderColumn,
  setOrderByUrl,
  currentPage,
  setCurrentPage,
  setTotalPages,
  scrollToTop,
  teams,
  teamData,
}: Props) => {
  const { selected, setSelectedTeam, selectedTeam } = useDashboardStore();
  const { expanded } = useTeamTableContext();
  const [isHover, setIsHover] = useState(false);
  const [orderBy, setOrderBy] = useState<string>(initialOrderBy || '');
  const { selectedTeams, setSelectedTeams } = useTeamTableStore();
  const [isDateChanging, setIsDateChanging] = useState(false);
  const { colors } = useTheme();

  //cache for storing already fetched team data (only for non-sorted scenarios)
  const [cachedData, setCachedData] = useState<{
    rows: TeamMetricRow[];
    fetchedTeams: Set<string>;
    lastFetchParams: string;
  }>({
    rows: [],
    fetchedTeams: new Set(),
    lastFetchParams: '',
  });

  useEffect(() => {
    const currentParams = JSON.stringify({
      startDate: startDate || selected.startDate,
      endDate: endDate || selected.endDate,
      sprintId: selected.sprintId,
      teamId: selectedTeam.value,
    });

    if (
      cachedData.lastFetchParams &&
      currentParams !==
        cachedData.lastFetchParams.slice(0, currentParams.length)
    ) {
      setIsDateChanging(true);
    }
  }, [
    startDate,
    endDate,
    selected.startDate,
    selected.endDate,
    selected.sprintId,
    selectedTeam.value,
  ]);

  const getVisibleTeamsInOrder = useCallback(
    (teamStructure: TeamDto[]) => {
      const orderedTeams: string[] = [];

      const traverse = (teams: TeamDto[]) => {
        teams.forEach((team) => {
          orderedTeams.push(team.name);

          if (
            team.subTeams &&
            team.subTeams.length > 0 &&
            expanded.includes(team.name)
          ) {
            traverse(team.subTeams);
          }
        });
      };

      traverse(teamStructure);
      return orderedTeams;
    },
    [expanded]
  );

  const teamsForAPI = useMemo(() => {
    if (orderColumn) {
      if (orderColumn === selectedMetric.id) {
        return [];
      } else {
        return selectedTeams || teams;
      }
    } else {
      return getVisibleTeamsInOrder(teamData || []);
    }
  }, [
    orderColumn,
    selectedMetric.id,
    selectedTeams,
    teams,
    expanded,
    teamParentMap,
    teamData,
  ]);

  const shouldUseCache = !orderColumn;

  const teamsToFetch = useMemo(() => {
    if (!shouldUseCache) {
      return {
        teams: teamsForAPI,
        shouldResetCache: true,
        fetchParams: '',
      };
    }

    const currentFetchParams = JSON.stringify({
      startDate: startDate || selected.startDate,
      endDate: endDate || selected.endDate,
      sprintId: selected.sprintId,
      teamId: selectedTeam.value,
      metricKey:
        selectedMetric.metricKey === 'CUSTOM'
          ? selectedMetric.metricTitle
          : selectedMetric.metricKey,
      isPerMember: selectedMetric.metricTitle.includes('/Member'),
    });

    if (currentFetchParams !== cachedData.lastFetchParams) {
      return {
        teams: teamsForAPI,
        shouldResetCache: true,
        fetchParams: currentFetchParams,
      };
    }

    const newTeams = teamsForAPI.filter(
      (team: string) => !cachedData.fetchedTeams.has(team)
    );

    return {
      teams: newTeams,
      shouldResetCache: false,
      fetchParams: currentFetchParams,
    };
  }, [
    shouldUseCache,
    teamsForAPI,
    cachedData.fetchedTeams,
    cachedData.lastFetchParams,
    startDate,
    endDate,
    selected.startDate,
    selected.endDate,
    selected.sprintId,
    selectedTeam.value,
    selectedMetric.metricKey,
    selectedMetric.metricTitle,
  ]);

  const shouldFetch = teamsToFetch.teams.length > 0 || !shouldUseCache;

  const { data: apiData, isLoading: isFetchingNew } = useTeamComparisonGql(
    startDate || selected.startDate,
    endDate || selected.endDate,
    selected.sprintId,
    selectedTeam.value,
    shouldFetch ? teamsToFetch.teams : [],
    selectedMetric.metricKey === 'CUSTOM'
      ? selectedMetric.metricTitle
      : selectedMetric.metricKey,
    selectedMetric.metricTitle.includes('/Member'),
    orderColumn === selectedMetric.id ? orderBy : '',
    orderColumn === selectedMetric.id ? currentPage || 0 : 0
  );

  useEffect(() => {
    if (apiData && shouldFetch && shouldUseCache) {
      setCachedData((prev) => {
        if (teamsToFetch.shouldResetCache) {
          setIsDateChanging(false);
          return {
            rows: apiData.rows || [],
            fetchedTeams: new Set(teamsToFetch.teams),
            lastFetchParams: teamsToFetch.fetchParams,
          };
        } else {
          const mergedRows = [...prev.rows, ...(apiData.rows || [])];
          const newFetchedTeams = new Set([
            ...prev.fetchedTeams,
            ...teamsToFetch.teams,
          ]);

          return {
            rows: mergedRows,
            fetchedTeams: newFetchedTeams,
            lastFetchParams: teamsToFetch.fetchParams,
          };
        }
      });
    }
    if (apiData && !shouldUseCache) {
      setIsDateChanging(false);
    }
  }, [apiData, shouldFetch, shouldUseCache, teamsToFetch]);

  const data = useMemo(() => {
    if (!shouldUseCache) {
      return apiData;
    }

    if (cachedData.rows.length > 0) {
      const visibleRows = cachedData.rows.filter((row) =>
        teamsForAPI.includes(row.team)
      );

      return {
        ...apiData,
        rows: visibleRows,
      };
    }

    return apiData;
  }, [shouldUseCache, apiData, cachedData.rows, teamsForAPI]);

  //resetting cache when metric changes
  useEffect(() => {
    setCachedData({
      rows: [],
      fetchedTeams: new Set(),
      lastFetchParams: '',
    });
  }, [selectedMetric.id, selectedMetric.metricKey]);

  useEffect(() => {
    if (
      data &&
      ['asc', 'desc'].includes(orderBy) &&
      orderColumn === selectedMetric.id
    ) {
      setTotalPages?.(data?.totalPages ?? 1);
      // setSelectedTeam({
      //   value: '',
      //   label: 'Organization',
      // });
      setSelectedTeams(data.rows.map((item: TeamMetricRow) => item.team));
    }
  }, [data]);

  useEffect(() => {
    if (orderColumn !== selectedMetric.id) {
      setOrderBy('');
    }
  }, [orderColumn]);

  return (
    <Flex
      direction="column"
      position="sticky"
      left={0}
      bg="white"
      h={'fit-content'}
      minW={
        selectedMetric.metricKey === 'DORA_ACTIVITY'
          ? '280px'
          : ['GIT_ACTIVITY', 'ISSUE_ACTIVITY', 'AI_ADOPTION'].includes(
              selectedMetric.metricKey
            )
          ? '250px'
          : '220px'
      }
      zIndex={80}
    >
      <Flex
        bg="#FBFBFB"
        position="sticky"
        top={0}
        borderLeft="1px solid #E0E0E0"
        borderRight={
          selectedMetrics[selectedMetrics.length - 1].metricKey ===
          selectedMetric.metricKey
            ? '1px solid #E0E0E0'
            : undefined
        }
        align="center"
        minH="52px"
        px={4}
        zIndex={50}
      >
        <Flex w="full" align="center" gap={3} justify={'center'}>
          <Flex
            w="full"
            cursor={
              DATA_POINT_KEYS.includes(selectedMetric.metricKey)
                ? 'default'
                : 'pointer'
            }
            gap={1}
            align="center"
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
            onClick={() => {
              if (DATA_POINT_KEYS.includes(selectedMetric.metricKey)) {
                return;
              }
              if (orderBy === '') {
                setOrderBy?.('asc');
                setOrderByUrl?.('asc');
                scrollToTop?.();
                setOrderColumn?.(selectedMetric.id ?? selectedMetric.metricKey);
                setIsHover(false);
              } else if (orderBy === 'asc') {
                setOrderBy?.('desc');
                setOrderByUrl?.('desc');
                setCurrentPage?.(0);
                scrollToTop?.();
                setIsHover(false);
                setOrderColumn?.(selectedMetric.id ?? selectedMetric.metricKey);
              } else if (orderBy === 'desc') {
                setOrderBy?.('');
                setOrderByUrl?.('');
                setOrderColumn?.('');
                scrollToTop?.();
                setIsHover(false);
                setSelectedTeams([]);
              }
            }}
          >
            {!DATA_POINT_KEYS.includes(selectedMetric.metricKey) &&
              orderColumn !==
                (selectedMetric.id ?? selectedMetric.metricKey) && (
                <Tooltip
                  hasArrow
                  label={`Sort by ${selectedMetric.metricTitle}`}
                  isOpen={isHover}
                >
                  <Icon w={19} h={19}>
                    <SortIcon
                      width={25}
                      height={25}
                      color={colors.text.secondary}
                    />
                  </Icon>
                </Tooltip>
              )}
            {!DATA_POINT_KEYS.includes(selectedMetric.metricKey) &&
              orderColumn === selectedMetric.id && (
                <Tooltip
                  hasArrow
                  label={
                    orderBy === 'asc' ? `Sorted Ascending` : 'Sorted Descending'
                  }
                  isOpen={isHover}
                >
                  <Icon w={19} h={19}>
                    {orderBy === 'asc' ? (
                      <SortAsscending
                        width={25}
                        height={25}
                        color={colors.text.secondary}
                      />
                    ) : (
                      <SortDescending
                        width={25}
                        height={25}
                        color={colors.text.secondary}
                      />
                    )}
                  </Icon>
                </Tooltip>
              )}
            <Text
              fontSize="sm"
              fontFamily="heading"
              fontWeight="medium"
              color="text.secondary2"
              w="fit-content"
              noOfLines={1}
              wordBreak="break-all"
            >
              {selectedMetric.metricTitle}
            </Text>
          </Flex>
          {metricTooltip?.[0]?.info && (
            <Tooltip label={metricTooltip?.[0]?.info} hasArrow>
              <Box>
                <InfoIcon width={17} height={17} />
              </Box>
            </Tooltip>
          )}
        </Flex>
      </Flex>

      <Divider />

      {(() => {
        if (
          isTeamsDataLoading ||
          (!data && isFetchingNew) ||
          (isDateChanging && isFetchingNew)
        ) {
          return [...Array(10)].map((_, index) => (
            <MetricLoadingCell key={index} />
          ));
        }

        const teamDataMap = new Map();
        data?.rows?.forEach((row: TeamMetricRow) => {
          teamDataMap.set(row.team, row);
        });

        const orderedVisibleTeams = getVisibleTeamsInOrder(teamData || []);

        const teamsBeingFetched = new Set(teamsToFetch.teams || []);

        return orderedVisibleTeams
          .map((teamName, index) => {
            const rowData = teamDataMap.get(teamName);

            if (rowData) {
              return (
                <TrendCell
                  key={teamName}
                  metricList={metricList || []}
                  data={rowData.metricRowDataList || []}
                  team={rowData.team}
                  title={selectedMetric.metricTitle}
                  compareTo={compareTo || ''}
                  showRightBorder={
                    selectedMetrics[selectedMetrics.length - 1].metricKey ===
                    selectedMetric.metricKey
                  }
                  metric={selectedMetric.metricKey}
                  moveToolTip={orderedVisibleTeams.length - 1 === index}
                  rowHeight={'184px'}
                  startDate={startDate}
                  endDate={endDate}
                  componentOrigin="team-table"
                />
              );
            }

            if (teamsBeingFetched.has(teamName) && isFetchingNew) {
              return <MetricLoadingCell key={`loading-${teamName}`} />;
            }

            return null;
          })
          .filter(Boolean);
      })()}
    </Flex>
  );
};

export default TeamMetric;
